/**
 * Schema.org Data Structures
 * Datos estructurados para mejorar SEO sin cambios visuales
 */

// Schema principal del negocio médico
export const medicalBusinessSchema = {
  "@context": "https://schema.org",
  "@type": ["MedicalBusiness", "LocalBusiness", "HealthAndBeautyBusiness"],
  "name": "Conectemos Chile",
  "alternateName": "Clínica Conectemos",
  "description": "Clínica de atención psicológica especializada en terapia individual, de pareja y tratamiento de trastornos de salud mental en Santiago, Chile.",
  "url": "https://www.conectemoschile.cl",
  "@id": "https://www.conectemoschile.cl/#organization",
  "logo": {
    "@type": "ImageObject",
    "url": "https://www.conectemoschile.cl/assets/img/logo.webp",
    "width": 400,
    "height": 400
  },
  "image": [
    "https://www.conectemoschile.cl/assets/img/oficina/ofi_largo.webp",
    "https://www.conectemoschile.cl/assets/img/terapista_angel.webp"
  ],
  "priceRange": "$32,000 - $46,000 CLP",
  "paymentAccepted": ["Efectivo", "Tarjeta de Crédito", "Tarjeta de Débito", "Transferencia Bancaria"],
  "currenciesAccepted": "CLP",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Santiago Centro, Metro Universidad de Chile",
    "addressLocality": "Santiago",
    "addressRegion": "Región Metropolitana",
    "postalCode": "8320000",
    "addressCountry": "CL"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": -33.4413404,
    "longitude": -70.6535814
  },
  "telephone": "+56-9-2199-1963",
  "email": "conectemoscl@gmail.com",
  "openingHours": [
    "Mo-Fr 09:00-20:00",
    "Sa 09:00-14:00"
  ],
  "medicalSpecialty": [
    "Psicología Clínica",
    "Terapia de Parejas",
    "Terapia Individual", 
    "Sexología Clínica",
    "Tratamiento de Ansiedad",
    "Tratamiento de Depresión",
    "Terapia Online"
  ],
  "serviceType": [
    "Terapia Psicológica",
    "Consulta Psicológica",
    "Terapia Online",
    "Terapia Presencial",
    "Evaluación Psicológica"
  ],
  "founder": {
    "@type": "Person",
    "name": "Ángel Díaz",
    "jobTitle": "Director y Psicólogo Clínico",
    "@id": "https://www.conectemoschile.cl/terapista/angel-diaz/#person"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Servicios de Terapia Psicológica",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Terapia Individual",
          "description": "Sesiones individuales de 50 minutos para tratamiento personalizado de ansiedad, depresión y otros trastornos"
        },
        "price": "36000",
        "priceCurrency": "CLP",
        "availability": "https://schema.org/InStock"
      },
      {
        "@type": "Offer", 
        "itemOffered": {
          "@type": "Service",
          "name": "Terapia de Pareja",
          "description": "Sesiones de pareja de 90 minutos para mejorar la comunicación y resolver conflictos"
        },
        "price": "46000",
        "priceCurrency": "CLP",
        "availability": "https://schema.org/InStock"
      }
    ]
  }
};

// Schema para Ángel Díaz como profesional
export const angelDiazPersonSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": "https://www.conectemoschile.cl/terapista/angel-diaz/#person",
  "name": "Ángel Díaz",
  "givenName": "Ángel",
  "familyName": "Díaz", 
  "jobTitle": "Director y Psicólogo Clínico",
  "description": "Psicólogo clínico especializado en terapia individual, de pareja y sexología clínica con más de 10 años de experiencia.",
  "url": "https://www.conectemoschile.cl/terapista/angel-diaz",
  "image": "https://www.conectemoschile.cl/assets/img/terapista_angel.webp",
  "worksFor": {
    "@type": "MedicalBusiness",
    "name": "Conectemos Chile",
    "@id": "https://www.conectemoschile.cl/#organization"
  },
  "hasOccupation": {
    "@type": "Occupation",
    "name": "Psicólogo Clínico",
    "occupationalCategory": "29-1066",
    "description": "Especialista en salud mental que proporciona terapia psicológica"
  },
  "knowsAbout": [
    "Psicología Clínica",
    "Terapia de Parejas",
    "Sexología Clínica", 
    "Tratamiento de Ansiedad",
    "Tratamiento de Depresión",
    "Terapia Cognitivo Conductual"
  ],
  "alumniOf": "Universidad de Chile",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Santiago",
    "addressRegion": "Región Metropolitana",
    "addressCountry": "CL"
  }
};

// Schema dinámico para cualquier terapeuta del equipo
export const createTherapistPersonSchema = (therapist: {
  id: string;
  name: string;
  title: string;
  description: string;
  image: string;
  specialties: string[];
  personalInfo?: { education: Array<{ institution: string }> };
  professionalInfo?: { licenseNumber?: string };
}) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `https://www.conectemoschile.cl/terapista/${therapist.id}/#person`,
  "name": therapist.name,
  "jobTitle": therapist.title,
  "description": therapist.description,
  "url": `https://www.conectemoschile.cl/terapista/${therapist.id}`,
  "image": `https://www.conectemoschile.cl${therapist.image}`,
  "worksFor": {
    "@type": "MedicalBusiness",
    "name": "Conectemos Chile",
    "@id": "https://www.conectemoschile.cl/#organization"
  },
  "hasOccupation": {
    "@type": "Occupation",
    "name": "Psicólogo/a Clínico/a",
    "occupationalCategory": "29-1066",
    "description": "Especialista en salud mental que proporciona terapia psicológica"
  },
  "knowsAbout": therapist.specialties,
  ...(therapist.personalInfo?.education[0]?.institution
    ? { "alumniOf": therapist.personalInfo.education[0].institution }
    : {}),
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Santiago",
    "addressRegion": "Región Metropolitana",
    "addressCountry": "CL"
  }
});

// Schema para la página web como Medical WebPage
export const medicalWebPageSchema = {
  "@context": "https://schema.org",
  "@type": "MedicalWebPage",
  "name": "Conectemos Chile - Clínica de Atención Psicológica",
  "description": "Clínica especializada en salud mental con profesionales certificados en Santiago, Chile",
  "url": "https://www.conectemoschile.cl",
  "medicalAudience": {
    "@type": "PeopleAudience",
    "audienceType": "Pacientes que buscan atención psicológica"
  },
  "about": {
    "@type": "MedicalCondition",
    "name": "Trastornos Psicológicos",
    "alternateName": ["Ansiedad", "Depresión", "Estrés", "Problemas de Pareja"]
  },
  "reviewedBy": {
    "@type": "Person",
    "name": "Ángel Díaz",
    "@id": "https://www.conectemoschile.cl/terapista/angel-diaz/#person"
  },
  "datePublished": "2023-01-01",
  "dateModified": "2026-03-13",
  "inLanguage": "es-CL"
};

// FAQ Schema para preguntas frecuentes
export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "name": "Preguntas Frecuentes - Conectemos Chile",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "¿Cuánto cuesta una sesión de terapia en Conectemos Chile?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Una sesión individual presencial cuesta $32.000 CLP pagando con transferencia o efectivo y $36.000 CLP con débito o crédito (sesión extendida $41.000 CLP). La terapia de pareja cuesta $36.000 CLP con transferencia o efectivo y $38.750 CLP con débito o crédito (sesión extendida $41.000 CLP con transferencia o efectivo y $46.000 CLP con débito o crédito). Las sesiones online tienen el mismo valor con cualquier método de pago. Aceptamos efectivo, tarjetas y transferencias bancarias."
      }
    },
    {
      "@type": "Question",
      "name": "¿Puedo pagar la terapia con tarjeta de débito o crédito?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Sí. En nuestra consulta en Santiago Centro aceptamos tarjetas de débito y crédito, además de efectivo. También puedes pagar por transferencia bancaria. Al agendar eliges \"Transferencia / Efectivo\" (valor preferente) o \"Débito / Crédito\"."
      }
    },
    {
      "@type": "Question",
      "name": "¿Cuánto dura una sesión de terapia?",
      "acceptedAnswer": {
        "@type": "Answer", 
        "text": "Las sesiones estándar duran 45-50 minutos según el tipo de terapia, con opción de sesión extendida de 90 minutos. Este tiempo permite un trabajo terapéutico efectivo y profundo."
      }
    },
    {
      "@type": "Question",
      "name": "¿Atienden de forma presencial y online?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Sí, ofrecemos tanto atención presencial en nuestras consultas en Santiago Centro como terapia online a través de videollamada segura."
      }
    },
    {
      "@type": "Question",
      "name": "¿Qué puedo esperar en mi primera consulta?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "En la primera consulta realizaremos una evaluación inicial donde conoceremos tu situación, estableceremos objetivos terapéuticos y definiremos el plan de tratamiento más adecuado para ti."
      }
    },
    {
      "@type": "Question",
      "name": "¿Cómo puedo agendar una cita?",
      "acceptedAnswer": {
        "@type": "Answer", 
        "text": "Puedes agendar tu cita a través de nuestro sistema online en la página web, por WhatsApp al +56 9 2199 1963 o escribiéndonos a conectemoscl@gmail.com."
      }
    }
  ]
};

// Breadcrumb schema para navegación
export const createBreadcrumbSchema = (items: Array<{name: string, url?: string}>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url ? `https://www.conectemoschile.cl${item.url}` : undefined
  }))
});

// Website schema para la organización
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://www.conectemoschile.cl/#website",
  "url": "https://www.conectemoschile.cl",
  "name": "Conectemos Chile",
  "description": "Clínica de atención psicológica en Santiago",
  "publisher": {
    "@id": "https://www.conectemoschile.cl/#organization"
  },
  "potentialAction": [
    {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint", 
        "urlTemplate": "https://www.conectemoschile.cl/search?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  ],
  "inLanguage": "es-CL"
};